/// <amd-module name="Test/Analytics/CardMetricsCommon/ControllerViews/CardMetrics" />
import { AnalyticsExceptionType, recognizeAnalyticsException } from "Analytics/Common/Utilities/AnalyticsExceptionUtilities";
import { ITrendData, TrendType } from "Analytics/Shared/Common/Types";
import * as AnalyticsResources from "Analytics/Shared/Common/Resources";
import { getTrendFromData } from "Analytics/Shared/Common/Utils/TrendCalculator";
import * as Utils_Number from "Analytics/Shared/Utils/Number";
import { AreaChartHelper } from "Analytics/Shared/Visuals/AreaChartView/Common/AreaChartHelper";
import { AreaChartView } from "Analytics/Shared/Visuals/AreaChartView/ControllerViews/AreaChartView";
import { CardMetricsVisual } from "Analytics/Shared/Visuals/CardMetricsVisual/CardMetricsVisual";
import * as React from "react";
import { CardMetricsChartSeries } from "Test/Analytics/CardMetricsCommon/CardMetricsChartSeries";
import * as Resources from "Test/Analytics/CardMetricsCommon/Resources";
import { CardMetricsSource } from "Test/Analytics/CardMetricsCommon/Sources/CardMetricsSource";
import { AnnouncementActions } from "Test/Analytics/Common/Actions/AnnouncementActions";
import { AnnouncementStore } from "Test/Analytics/Common/Stores/AnnouncementStore";
import { ICardMetricsData } from "Test/Analytics/Common/Types";
import * as CommonTypes from "Test/Analytics/Common/Types";
import * as TcmTelemetryConstants from "Test/Analytics/Common/Utils/TcmTelemetryConstants";
import { publishEvent } from "Test/Analytics/Common/Utils/TelemetryService";
import { ITrendChartData } from "Test/Analytics/TrendChartCommon/Types";
import { TestReportDataParser } from "Test/common-util/Common.Utils";
import * as TCMContracts from "Test/common-util/Contracts/Test";
import { FilterValueItem } from "Test/common-util/TestResults.Filtering.Common";
import { FailedTestsMetricSource } from "Test/FailedTestsMetricView/Sources/FailedTestsMetricSource";
import { announce } from "VSS/Core/Util/Accessibility";
import { format, localeFormat } from "VSS/Core/Util/String";
import { ChartProps } from "VSS/Features/Charts/Contracts";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { Components, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";

export interface ICardMetricsProps extends IVssComponentProperties {
    definitionId: number;
    contextType: string;
    footerText?: string;
    errorText?: string;
    onCardClick: Function;
}

export interface ICardMetricsState {
    testOutcomeMetricsData: ICardMetricsData | undefined;
    uniqueFailingTests: number | undefined;
    totalFailedResults: number | undefined;
    chartData?: ITrendChartData;
    trend?: ITrendData;
    errorText?: string;
    info?: string;
    iconName?: string;
}

export class CardMetrics extends VssComponent<ICardMetricsProps, ICardMetricsState> {
    private static readonly _analyticsHubRouteUrl: string = "ms.vss-test-analytics-web.new-test-analytics-route";

    private _announcementStore: AnnouncementStore;
    private _cardMetricsDataPromise: Promise<CommonTypes.ICardMetricsData>;
    private _failedTestMetricsPromise: Promise<CommonTypes.IFailedCardMetricData>;
    private _testReportContext: CommonTypes.ITestReportContext;
    private _defaultConfValues: CommonTypes.IReportConfiguration;
    private _trendChartDataPromise: Promise<ITrendChartData>;
    private _isTrendGood: boolean;

    constructor(props: ICardMetricsProps) {
        super(props);
        this._announcementStore = new AnnouncementStore(new AnnouncementActions());
        this._announcementStore.addChangedListener(this._onAnnouncementUpdate);
        this.state = { errorText: this.props.errorText } as Readonly<ICardMetricsState>;
    }

    public async componentDidMount() {
        super.componentDidMount();
        if (!this.props.errorText) {
            await this._refreshData();
        }
    }

    public render(): JSX.Element {
        const errorText: string = this.props.errorText || this.state.errorText || "";

        return (
            <CardMetricsVisual
                title={`${Resources.AnalyticsCardMetricsHeaderText}`}
                dataValidationPromises={[this._cardMetricsDataPromise, this._failedTestMetricsPromise]}
                primaryMetric={
                    this.state.testOutcomeMetricsData && {
                        suffix: Resources.Percentage,
                        value: TestReportDataParser.getPercentageInDisplayFormatWithoutSymbol(
                            Number(this.state.testOutcomeMetricsData.passPercentage),
                            2
                        )
                    }
                }
                secondaryMetric={
                    this.state.testOutcomeMetricsData && {
                        label: `${Resources.TotalTestResultsText}`,
                        value: Utils_Number.formatAbbreviatedNumber(this.state.testOutcomeMetricsData.totalCount).toLocaleString()
                    }
                }
                trend={this.state.trend}
                isTrendIncreaseGood={true}
                insight={this.state.uniqueFailingTests ? this._getUniqueFailingTestsDisplayName() : Resources.NoTestFailure}
                subtitle={this._getFooterText()}
                onCardClick={this._onCardClick}
                errorText={this.state.errorText}
                info={this.state.info}
                iconName={this.state.iconName}
            >
                {this._verifyChartData(this.state.chartData) && (
                    <AreaChartView chartOptions={this._getChartOptions(this.state.chartData!)} />
                )}
            </CardMetricsVisual>
        );
    }

    public componentDidUpdate(previousProps: ICardMetricsProps, previousState: ICardMetricsState) {
        super.componentDidUpdate(previousProps, previousState);
        if (previousProps.definitionId !== this.props.definitionId) {
            this.setState({
                errorText: undefined,
                testOutcomeMetricsData: undefined,
                trend: undefined,
                chartData: undefined,
                info: undefined
            });
            this._refreshData();
        }
    }

    public componentWillUnmount() {
        this._announcementStore.dispose();
    }

    private _getUniqueFailingTestsDisplayName(): string {
        return localeFormat(
            Resources.TestAnalyticsLinkMessage,
            this.state.uniqueFailingTests,
            this.state.uniqueFailingTests === 1 ? Resources.TestSingularText : Resources.TestPluralText,
            this.state.totalFailedResults != null ? this.state.totalFailedResults : ""
        );
    }

    private _getChartOptions = (chartData: ITrendChartData): ChartProps => {
        const _areaChartHelper: AreaChartHelper = new AreaChartHelper();
        const chartSeries = new CardMetricsChartSeries(chartData.secondaryChartData!.map(d => d.aggrValue));
        let chartProps = _areaChartHelper.getAreaChartOptions(this.context, chartData.secondaryChartData!, chartSeries, true);
        chartProps = _areaChartHelper.setGradientFill(chartProps, chartSeries.color);
        return chartProps;
    };

    private _verifyChartData = (chartData: ITrendChartData | undefined): boolean => {
        return chartData && chartData.secondaryChartData && chartData.secondaryChartData.length > 1 ? true : false;
    };

    private async _refreshData(): Promise<void> {
        this._testReportContext = {
            contextType: this.props.contextType == "build" ? TCMContracts.TestResultsContextType.Build : TCMContracts.TestResultsContextType.Release,
            definitionId: this.props.definitionId
        } as CommonTypes.ITestReportContext;

        this._defaultConfValues = {
            groupBy: CommonTypes.GroupBy.None,
            trendBy: CommonTypes.TrendBy.Days,
            period: CommonTypes.Period.Days_14,
            configuredFilters:
                this.props.contextType == "build"
                    ? {
                          [CommonTypes.Filter.Workflow.toString()]: {
                              values: [new FilterValueItem(CommonTypes.Workflow.Build, CommonTypes.Workflow[CommonTypes.Workflow.Build])]
                          }
                      }
                    : {}
        } as CommonTypes.IReportConfiguration;
        const cardMetricsSource = new CardMetricsSource(this.context.pageContext);
        const _failedTestsMetricSource = new FailedTestsMetricSource(this.context.pageContext);
        this._failedTestMetricsPromise = _failedTestsMetricSource.queryTotalFailingTestsMetricData(this._testReportContext, this._defaultConfValues);
        this._cardMetricsDataPromise = cardMetricsSource.queryTestOutcomeCountMetricDataTestRunTable(
            this._testReportContext,
            this._defaultConfValues
        );

        this._trendChartDataPromise = cardMetricsSource.queryDefaultTrendChartData(this._testReportContext);
        this._trendChartDataPromise.then(trendChartData => {
            let trend = undefined;
            if (this._verifyChartData(trendChartData)) {
                const len = trendChartData.secondaryChartData!.length;
                const startValue = trendChartData.secondaryChartData![0].aggrValue;
                const endValue = trendChartData.secondaryChartData![len - 1].aggrValue;
                trend = getTrendFromData(startValue, endValue, false);
                this._isTrendGood = trend && trend.trendType == TrendType.Increased ? true : false;
            }
            this.setState({ chartData: trendChartData, trend: trend });
        });

        Promise.all([this._failedTestMetricsPromise, this._cardMetricsDataPromise])
            .then(resolvedData => {
                const failedTestMetricData = resolvedData[0];
                const cardMetricsData = resolvedData[1];
                if (cardMetricsData.totalCount > 0) {
                    this.setState({
                        uniqueFailingTests: failedTestMetricData.totalFailingTestsCount,
                        totalFailedResults: cardMetricsData.failedCount,
                        testOutcomeMetricsData: cardMetricsData,
                        errorText: undefined,
                        info: undefined,
                        iconName: undefined
                    });
                } else {
                    this.setState({ info: Resources.AnalyticsCardTestsNotFoundMessage, iconName: "TestBeakerSolid" });
                }
            })
            .catch(error => {
                let errorText: string = error.toString();
                let recognizedException = recognizeAnalyticsException(error);
                if (recognizedException === AnalyticsExceptionType.DataNotReady) {
                    errorText = Resources.AnalyticsUnavailableCardMetricsSuggestion;
                }
                if (recognizedException === AnalyticsExceptionType.Disabled) {
                    errorText = format("{0}{1}", AnalyticsResources.AnalyticsNotEnabledMessage, AnalyticsResources.AnalyticsNotEnabledSuggestion);
                }
                this.setState({ errorText: errorText, iconName: "ErrorBadge" });
            });
    }

    private _onCardClick = (event?: React.MouseEvent<HTMLDivElement>): void => {
        if (this.props.onCardClick) {
            this.props.onCardClick(CardMetrics._analyticsHubRouteUrl, event);
        }

        publishEvent(this.context.pageContext, TcmTelemetryConstants.featureTestAX_ReportClick, {}, this._testReportContext, this._defaultConfValues);
    };

    private _getFooterText(): string {
        return this.props.footerText ? this.props.footerText : "";
    }

    private _onAnnouncementUpdate = () => {
        announce(this._announcementStore.getState().announcementText);
    };
}

Components.add("testCardMetrics", CardMetrics);
